import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, fmt, LOGO_URL } from "@/lib/api";
import { PackageCards } from "@/components/quote/PackageCards";
import { PartnerPackage } from "@/components/quote/PartnerPackage";
import { PaymentSection } from "@/components/quote/PaymentSection";
import { ShowcaseHero } from "@/components/quote/ShowcaseHero";
import { CalendarDays, ArrowRight, ExternalLink, CheckCircle2 } from "lucide-react";

const PKG_LABELS = { essential: "Essential Revival", complete: "Complete Revival", signature: "Signature Revival", partner: "Signature Revival" };

const SectionTitle = ({ kicker, title }) => (
  <div className="text-center mb-12">
    {kicker && <p className="text-[11px] uppercase tracking-[0.3em] text-[#c19a6b] font-bold mb-3">{kicker}</p>}
    <h2 className="font-display text-3xl sm:text-4xl tracking-tight">{title}</h2>
    <div className="w-12 h-[2px] bg-[#c19a6b] mx-auto mt-5" />
  </div>
);

const Stage = ({ title, sub }) => (
  <div className="flex-1 text-center px-4">
    <h3 className="font-display text-2xl sm:text-3xl tracking-[0.1em]">{title}</h3>
    <p className="text-sm text-gray-500 mt-2 leading-relaxed">{sub}</p>
  </div>
);

export default function CustomerQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [paymentOption, setPaymentOption] = useState("deposit");
  const [reserving, setReserving] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [payStatus, setPayStatus] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [showQuote, setShowQuote] = useState(false);
  const paymentRef = useRef();

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/public/quotes/${token}`)
      .then((r) => {
        setData(r.data);
        if (!r.data.expired) {
          setSelected(r.data.selected_package || r.data.recommended_package);
          if (r.data.payment_option) setPaymentOption(r.data.payment_option);
          if (r.data.invoice) setInvoice(r.data.invoice);
        }
      })
      .catch(() => setError(true));
  }, [token]);

  const pollStatus = useCallback(() => {
    axios.get(`${API}/public/quotes/${token}/payment-status`).then((r) => {
      if (r.data.has_invoice) {
        setPayStatus(r.data);
        if (r.data.public_url) setInvoice((inv) => ({ ...inv, public_url: r.data.public_url }));
      }
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!invoice) return;
    pollStatus();
    const t = setInterval(pollStatus, 15000);
    return () => clearInterval(t);
  }, [invoice, pollStatus]);

  const selectPackage = (pkg) => {
    setSelected(pkg);
    axios.post(`${API}/public/quotes/${token}/select`, { package: pkg, payment_option: paymentOption }).catch(() => {});
    setTimeout(() => paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const reserve = async () => {
    if (invoice && invoice.package === selected && invoice.payment_option === paymentOption) {
      setShowQuote(false);
      window.scrollTo({ top: 0 });
      return;
    }
    setReserving(true);
    try {
      const endpoint = invoice ? "change-selection" : "invoice";
      const { data: res } = await axios.post(`${API}/public/quotes/${token}/${endpoint}`, {
        package: selected,
        payment_option: paymentOption,
      });
      setInvoice(res.invoice);
      setPayStatus(null);
      setShowQuote(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : "We couldn't create your invoice. Please try again or contact ARK Furniture.");
    } finally {
      setReserving(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <img src={LOGO_URL} alt="ARK Furniture" className="h-14 mb-8" />
        <h1 className="font-display text-2xl mb-3">Quote not found</h1>
        <p className="text-gray-500 text-sm">Please check your link or contact ARK Furniture.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-black border-t-[#c19a6b] rounded-full animate-spin" />
      </div>
    );
  }

  if (data.expired) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center" data-testid="expired-quote-view">
        <img src={LOGO_URL} alt="ARK Furniture" className="h-14 mb-8" />
        <h1 className="font-display text-3xl mb-4">This quote has expired</h1>
        <p className="text-gray-500 max-w-md leading-relaxed">
          Please contact ARK Furniture for updated availability and current pricing.
        </p>
      </div>
    );
  }

  const pkg = selected ? data.pricing.packages[selected] : null;
  const paid = payStatus?.paid;
  const depositPaid = payStatus?.deposit_paid && !paid;

  // ---------- Confirmation / booked views ----------
  if (invoice && !showQuote) {
    const invPkg = data.pricing.packages[invoice.package];
    return (
      <div className="min-h-screen bg-white">
        <header className="py-10 text-center border-b border-gray-100">
          <img src={LOGO_URL} alt="ARK Furniture" className="h-12 mx-auto" />
        </header>
        <div className="max-w-xl mx-auto px-6 py-16 sm:py-24 text-center fade-up">
          {paid ? (
            <div data-testid="booked-view">
              <CheckCircle2 size={56} className="text-[#c19a6b] mx-auto mb-8" strokeWidth={1.2} />
              <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-6">You're booked!</h1>
              <p className="text-gray-600 leading-relaxed mb-4">
                Thank you, {data.first_name}. Your ARK Furniture Revival project has been reserved.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed">
                ARK will contact you shortly to coordinate pickup and the next steps for your {data.project_title}.
              </p>
            </div>
          ) : (
            <div data-testid="confirmation-view">
              <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-6">
                Your ARK Furniture Revival project is almost reserved.
              </h1>
              <p className="text-gray-600 leading-relaxed mb-10">
                Complete payment through the secure Square invoice to reserve your project.
              </p>
              {depositPaid && (
                <p className="bg-emerald-50 text-emerald-700 text-sm font-semibold py-3 px-4 mb-8" data-testid="deposit-paid-notice">
                  Deposit received — thank you! The remaining balance is due per your invoice schedule.
                </p>
              )}
              <div className="bg-[#FAF7F2] border border-[#c19a6b]/30 p-8 text-left space-y-3 mb-10" data-testid="invoice-summary">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Package</span><span className="font-semibold">{PKG_LABELS[invoice.package]}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Payment option</span><span className="font-semibold">{invoice.payment_option === "full" ? "Pay in Full (10% savings)" : "50% Deposit"}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Invoice total</span><span className="font-semibold">{fmt(invoice.total_cents)}</span></div>
                {invoice.payment_option === "deposit" && (
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Due today</span><span className="font-semibold text-[#c19a6b]">{fmt(invoice.deposit_cents)}</span></div>
                )}
                {data.pickup_date && (
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Next available pickup</span><span className="font-semibold">{data.pickup_date}</span></div>
                )}
              </div>
              {invoice.public_url ? (
                <a
                  data-testid="square-pay-btn"
                  href={invoice.public_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full h-14 bg-black text-white text-sm uppercase tracking-[0.2em] font-bold btn-lift"
                >
                  Continue to your Square invoice <ExternalLink size={16} />
                </a>
              ) : (
                <p className="text-sm text-gray-500">Your secure Square invoice is being prepared — this page will update automatically. Square has also emailed the invoice to you.</p>
              )}
              <p className="text-xs text-gray-400 mt-6">Payments are processed securely by Square. ARK never sees your card details.</p>
              {!depositPaid && (
                <button
                  data-testid="change-selection-btn"
                  onClick={() => {
                    setShowQuote(true);
                    setTimeout(() => document.getElementById("packages")?.scrollIntoView({ behavior: "smooth" }), 150);
                  }}
                  className="mt-8 text-sm text-[#8a6a42] hover:text-black underline underline-offset-4 transition-colors duration-200 block mx-auto"
                >
                  Changed your mind? Choose a different package or payment option
                </button>
              )}
            </div>
          )}
          <button
            data-testid="back-to-quote-btn"
            onClick={() => {
              setShowQuote(true);
              window.scrollTo({ top: 0 });
            }}
            className="mt-10 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black underline underline-offset-4 transition-colors duration-200"
          >
            ← View your full quote again
          </button>
        </div>
      </div>
    );
  }

  // ---------- Main quote experience ----------
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <header className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-8 flex items-center justify-between gap-4">
          <img src={LOGO_URL} alt="ARK Furniture" className="h-11" />
          <div className="flex items-center gap-3">
            {data.partner && (
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-[#c19a6b] border border-[#c19a6b]/40 px-3 py-1.5" data-testid="partner-program-badge">
                {data.partner.program_name}
              </span>
            )}
            <span className="text-[11px] uppercase tracking-[0.2em] text-gray-400" data-testid="quote-number">{data.quote_number}</span>
          </div>
        </div>
      </header>

      {invoice && (
        <div className="bg-[#c19a6b] text-white sticky top-0 z-40" data-testid="return-to-payment-banner">
          <div className="max-w-5xl mx-auto px-6 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-5 text-center">
            <span className="text-sm font-semibold tracking-wide">
              {paid
                ? `You're booked, ${data.first_name} — your project is reserved.`
                : `Your ${PKG_LABELS[invoice.package]} invoice is ready, ${data.first_name}.`}
            </span>
            <button
              data-testid="return-to-payment-btn"
              onClick={() => {
                setShowQuote(false);
                window.scrollTo({ top: 0 });
              }}
              className="bg-black text-white text-[11px] uppercase tracking-[0.18em] font-bold px-4 py-2 btn-lift"
            >
              {paid ? "View confirmation" : "Return to payment"}
            </button>
          </div>
        </div>
      )}

      {data.pickup_date && (
        <div className="bg-black text-white" data-testid="pickup-banner">
          <div className="max-w-5xl mx-auto px-6 py-3.5 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 text-center">
            <span className="inline-flex items-center gap-2 text-sm tracking-wide">
              <CalendarDays size={15} className="text-[#c19a6b]" />
              Next available pickup: <strong className="text-[#c19a6b]">{data.pickup_date}</strong>
            </span>
            <span className="hidden sm:inline text-white/30">·</span>
            <span className="text-xs text-white/60">Reserve your project to secure the next available pickup</span>
          </div>
        </div>
      )}

      <ShowcaseHero showcase={data.showcase} />

      <section className="max-w-5xl mx-auto px-6 pt-16 sm:pt-24 pb-12 text-center fade-up">
        {data.partner ? (
          <>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#c19a6b] font-bold mb-5">A Private Client Benefit From</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none" data-testid="hero-headline">
              {data.partner.realtor_name}
            </h1>
            {data.partner.brokerage && (
              <p className="text-[11px] uppercase tracking-[0.22em] text-gray-400 mt-4">{data.partner.brokerage}</p>
            )}
            <p className="font-display text-2xl sm:text-3xl mt-12 max-w-2xl mx-auto leading-snug" data-testid="partner-client-headline">
              {data.partner.headline_style === "B"
                ? `${data.partner.realtor_first_name || data.partner.realtor_name} takes care of their clients. So we do too.`
                : `Any friend of ${data.partner.realtor_name}'s is a friend of ours.`}
            </p>
            <p className="text-gray-600 text-[15px] mt-6 max-w-xl mx-auto leading-relaxed">
              {data.first_name}, as a client of {data.partner.realtor_name} you're receiving ARK's Signature Revival at
              our Essential Revival rate.
            </p>
            <p className="font-display italic text-lg text-gray-500 mt-8" data-testid="project-title">{data.project_title}</p>
          </>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#c19a6b] font-bold mb-5">ARK Furniture Revival</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl mx-auto" data-testid="hero-headline">
              {data.first_name}, let's bring your furniture back to life.
            </h1>
            <p className="font-display italic text-lg text-gray-500 mt-6" data-testid="project-title">{data.project_title}</p>
          </>
        )}
      </section>

      {data.photos.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-14" data-testid="photo-gallery">
          <div className={`grid gap-3 ${data.photos.length === 1 ? "grid-cols-1 max-w-2xl mx-auto" : data.photos.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`}>
            {data.photos.map((pid, i) => (
              <img
                key={pid}
                src={`${API}/photos/${pid}`}
                alt="Your furniture"
                className={`w-full object-cover ${i === 0 && data.photos.length > 2 ? "col-span-2 sm:col-span-1" : ""} aspect-[4/3]`}
              />
            ))}
          </div>
        </section>
      )}

      {data.assessment && (
        <section className="max-w-3xl mx-auto px-6 pb-20">
          <div className="bg-[#FAF7F2] border-l-2 border-[#c19a6b] p-8 sm:p-10" data-testid="assessment-block">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#c19a6b] font-bold mb-4">Our Assessment of Your Furniture</p>
            <p className="text-gray-700 leading-relaxed text-[15px]">{data.assessment}</p>
          </div>
        </section>
      )}

      {/* KEEP / REVIVE / PROTECT */}
      <section className="bg-black text-white py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-4">
            <Stage title="KEEP" sub="Your existing wood colour & character" />
            <ArrowRight className="text-[#c19a6b] rotate-90 md:rotate-0 shrink-0" size={22} />
            <Stage title="REVIVE" sub="Reduce years of surface wear" />
            <ArrowRight className="text-[#c19a6b] rotate-90 md:rotate-0 shrink-0" size={22} />
            <Stage title="PROTECT" sub="Apply ARK's professional protective finish" />
          </div>
          <div className="max-w-2xl mx-auto text-center mt-16">
            <h2 className="font-display text-2xl sm:text-3xl mb-6">What is resurfacing?</h2>
            <p className="text-white/70 leading-relaxed text-[15px]">{data.copy.resurfacing_explanation}</p>
            <p className="text-white/45 leading-relaxed text-sm mt-5 italic">{data.copy.resurfacing_note}</p>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="max-w-6xl mx-auto px-6 py-20 sm:py-28" id="packages">
        {data.partner ? (
          <SectionTitle kicker="Your Private Client Benefit" title="The Signature Revival, at the Essential rate" />
        ) : (
          <SectionTitle kicker="Your Three Revival Options" title="Choose the level of care that's right for you" />
        )}
        {data.items.length > 0 && (
          <p className="text-center text-sm text-gray-500 mb-10" data-testid="items-summary">
            {data.items.map((i) => `${i.qty} × ${i.label}`).join(" · ")}
            {data.pricing.show_adjustment && ` · project complexity adjustment ${fmt(data.pricing.adjustment_cents)}`}
          </p>
        )}
        {data.partner ? (
          <PartnerPackage
            pkg={data.pricing.packages.partner}
            partner={data.partner}
            copy={data.copy}
            selected={selected}
            onSelect={selectPackage}
          />
        ) : (
          <PackageCards
            pricing={data.pricing}
            copy={data.copy}
            recommended={data.recommended_package}
            selected={selected}
            onSelect={selectPackage}
          />
        )}
      </section>

      {data.partner && (
        <section className="bg-black text-white py-16 sm:py-20" data-testid="partner-moving-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#c19a6b] font-bold mb-3">Moving?</p>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Let us take the furniture off your moving list.</h2>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-10 md:gap-4">
              <Stage title="YOUR OLD HOME" sub="ARK picks up the furniture" />
              <ArrowRight className="text-[#c19a6b] rotate-90 md:rotate-0 shrink-0" size={22} />
              <Stage title="ARK WORKSHOP" sub="We revive it while you move" />
              <ArrowRight className="text-[#c19a6b] rotate-90 md:rotate-0 shrink-0" size={22} />
              <Stage title="YOUR NEW HOME" sub="We deliver it refreshed and ready" />
            </div>
            <p className="text-center text-white/50 text-sm mt-12">
              Just let ARK know your move dates when we confirm your project.
            </p>
          </div>
        </section>
      )}

      {/* Payment */}
      <section ref={paymentRef} className="bg-[#FAF7F2] py-20 sm:py-24 scroll-mt-4">
        <div className="max-w-5xl mx-auto px-6">
          <SectionTitle
            kicker={invoice ? PKG_LABELS[invoice.package] : pkg ? `${pkg.label} Selected` : "Almost There"}
            title={invoice ? "Your reservation is in progress" : "How would you like to reserve?"}
          />
          {invoice && (paid || depositPaid) ? (
            <div className="max-w-xl mx-auto text-center" data-testid="invoice-exists-notice">
              <p className="text-gray-600 leading-relaxed mb-8">
                You've already chosen the <strong>{PKG_LABELS[invoice.package]}</strong> with{" "}
                {invoice.payment_option === "full" ? "Pay in Full (10% savings)" : "a 50% deposit"} —{" "}
                {fmt(invoice.total_cents)} total. {paid ? "Your project is reserved." : "Your deposit has been received."}
              </p>
              <button
                onClick={() => {
                  setShowQuote(false);
                  window.scrollTo({ top: 0 });
                }}
                className="w-full h-14 bg-black text-white text-sm uppercase tracking-[0.2em] font-bold btn-lift"
              >
                View your confirmation
              </button>
            </div>
          ) : pkg ? (
            <>
              {invoice && (
                <p className="max-w-2xl mx-auto text-center text-sm text-gray-600 bg-white border border-[#c19a6b]/40 py-3 px-4 mb-8" data-testid="current-invoice-note">
                  Your current invoice: <strong>{PKG_LABELS[invoice.package]}</strong>,{" "}
                  {invoice.payment_option === "full" ? "Pay in Full" : "50% deposit"} — {fmt(invoice.total_cents)}.
                  Pick any option below to change it before paying.
                </p>
              )}
              <PaymentSection
                pkg={pkg}
                paymentOption={paymentOption}
                setPaymentOption={setPaymentOption}
                onReserve={reserve}
                reserving={reserving}
                pickupDate={data.pickup_date}
                changeMode={!!invoice && !(invoice.package === selected && invoice.payment_option === paymentOption)}
              />
            </>
          ) : (
            <p className="text-center text-gray-500">Select a package above to see your payment options.</p>
          )}
        </div>
      </section>

      {/* ARK Promise */}
      <section className="max-w-3xl mx-auto px-6 py-20 sm:py-24 text-center">
        <SectionTitle title="The ARK Resurfacing Promise" />
        <p className="text-gray-600 leading-relaxed" data-testid="ark-promise-text">{data.copy.ark_promise}</p>
      </section>

      {/* Before & After Gallery */}
      {gallery.length > 0 && (
        <section className="bg-[#FAF7F2] py-20 sm:py-24" data-testid="before-after-gallery">
          <div className="max-w-5xl mx-auto px-6">
            <SectionTitle kicker="Real ARK Revivals" title="Furniture we've brought back to life" />
            <div className={`grid gap-8 ${gallery.length === 1 ? "max-w-xl mx-auto" : "sm:grid-cols-2"}`}>
              {gallery.slice(0, 6).map((g) => (
                <div key={g.id} className="bg-white border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                  <div className="grid grid-cols-2 gap-1 p-1">
                    {[["Before", g.before_photo_id], ["After", g.after_photo_id]].map(([lab, pid]) => (
                      <div key={lab} className="relative">
                        <img src={`${API}/photos/${pid}`} alt={`${g.title} — ${lab}`} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                        <span className={`absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 ${lab === "After" ? "bg-[#c19a6b] text-white" : "bg-black/70 text-white"}`}>
                          {lab}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="font-display text-lg text-center py-4">{g.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why resurface */}
      <section className="bg-black text-white py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl mb-5">Why resurface instead of replace?</h2>
            <p className="text-white/60 leading-relaxed text-[15px]">
              If you already own quality furniture, you may be able to preserve the pieces you love rather than replacing them.
              Resurfacing renews and protects the furniture that already fits your home, your history, and your style.
            </p>
          </div>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl mb-5">Why not completely restore it?</h2>
            <p className="text-white/60 leading-relaxed text-[15px]">
              If you want to dramatically change the wood colour or completely remove the existing finish, a full restoration may
              be the better option. If you already love the colour and character of your furniture, resurfacing can often be the
              more appropriate service.
            </p>
            <p className="text-[#c19a6b] font-display italic mt-6">
              Restoration is for furniture you want to change. Resurfacing is for furniture you already love.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-6 py-20 sm:py-28">
        <SectionTitle kicker="Effortless From Start To Finish" title="How it works" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {["Choose Your Revival Package", "Reserve Your Project", "ARK Coordinates Pickup", "We Revive & Return Your Furniture"].map((step, i) => (
            <div key={i} className="text-center sm:text-left">
              <span className="font-display text-4xl text-[#c19a6b]/60">{i + 1}</span>
              <h3 className="font-display text-lg mt-3 leading-snug">{step}</h3>
            </div>
          ))}
        </div>
        <div className="text-center mt-16">
          <button
            data-testid="back-to-packages-btn"
            onClick={() => document.getElementById("packages").scrollIntoView({ behavior: "smooth" })}
            className="h-13 px-10 py-4 bg-[#c19a6b] text-white text-xs uppercase tracking-[0.2em] font-bold btn-lift"
          >
            Review Your Options
          </button>
        </div>
      </section>

      <footer className="border-t border-gray-100 py-12 text-center">
        <img src={LOGO_URL} alt="ARK Furniture" className="h-9 mx-auto mb-4 opacity-80" />
        <p className="text-xs text-gray-400 tracking-wide">Quote {data.quote_number} · Payments processed securely by Square</p>
      </footer>
    </div>
  );
}
